import { useEffect, useMemo, useState } from 'react';
import { Plus, Minus, Search, Package, Pencil } from 'lucide-react';
import {
  listarProdutos,
  criarProduto,
  ajustarEstoque,
  desativarProduto,
  atualizarProduto,
  type Produto,
} from '@/features/estoque/api';
import KPITile from '@/features/dashboard/KPITile';
import Money from '@/components/Money';
import Button from '@/components/Button';
import Pill from '@/components/Pill';
import { useToast } from '@/components/Toast';
import { cn } from '@/lib/cn';

const inputCls =
  'bg-input border border-[var(--border)] rounded-sm px-s3 h-[34px] font-body text-[13px] text-fg-1 outline-none focus:border-moss-400 w-full';

export default function ProdutosPage() {
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [filtro, setFiltro] = useState('');
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  const [editando, setEditando] = useState<Produto | null>(null);
  const toast = useToast();

  // form novo produto
  const [nome, setNome] = useState('');
  const [categoria, setCategoria] = useState('');
  const [preco, setPreco] = useState('');
  const [unidade, setUnidade] = useState('un');
  const [estoque, setEstoque] = useState('');
  const [minimo, setMinimo] = useState('');
  const [criando, setCriando] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const lista = await listarProdutos();
        if (alive) setProdutos(lista);
      } catch (err) {
        if (alive) toast('error', `Falha ao carregar catálogo — ${(err as Error).message}.`);
      }
    })();
    return () => {
      alive = false;
    };
  }, [toast]);

  const substituir = (p: Produto) =>
    setProdutos((arr) => arr.map((x) => (x.id === p.id ? p : x)));

  const visiveis = useMemo(() => {
    const t = filtro.trim().toLowerCase();
    return produtos.filter((p) => {
      if (!mostrarInativos && !p.ativo) return false;
      if (!t) return true;
      return `${p.nome} ${p.categoria || ''}`.toLowerCase().includes(t);
    });
  }, [produtos, filtro, mostrarInativos]);

  const kpis = useMemo(() => {
    const ativos = produtos.filter((p) => p.ativo);
    return {
      ativos: ativos.length,
      baixo: ativos.filter((p) => p.baixo).length,
      valor: ativos.reduce((s, p) => s + p.preco * p.estoque, 0),
    };
  }, [produtos]);

  async function onCriar(e: React.FormEvent) {
    e.preventDefault();
    if (!nome.trim()) {
      toast('error', 'Nome do produto é obrigatório.');
      return;
    }
    setCriando(true);
    try {
      const p = await criarProduto({
        nome: nome.trim(),
        categoria: categoria.trim() || null,
        preco: Number(preco) || 0,
        unidade: unidade.trim() || 'un',
        estoque: Number(estoque) || 0,
        estoque_minimo: Number(minimo) || 0,
      });
      setProdutos((arr) => [...arr, p].sort((a, b) => a.nome.localeCompare(b.nome)));
      toast('success', `${p.nome} adicionado ao catálogo.`);
      setNome('');
      setCategoria('');
      setPreco('');
      setUnidade('un');
      setEstoque('');
      setMinimo('');
    } catch (err) {
      toast('error', `Falha ao adicionar — ${(err as Error).message}.`);
    } finally {
      setCriando(false);
    }
  }

  async function onAjustar(p: Produto, delta: number) {
    setBusy(p.id);
    try {
      substituir(await ajustarEstoque(p.id, delta, delta > 0 ? 'reposição' : 'baixa manual'));
    } catch (err) {
      toast('error', `Falha no ajuste — ${(err as Error).message}.`);
    } finally {
      setBusy(null);
    }
  }

  async function onToggleAtivo(p: Produto) {
    setBusy(p.id);
    try {
      const atualizado = p.ativo ? await desativarProduto(p.id) : await atualizarProduto(p.id, { ativo: true });
      substituir(atualizado);
      toast('success', `${p.nome} ${atualizado.ativo ? 'reativado' : 'desativado'}.`);
    } catch (err) {
      toast('error', `Falha — ${(err as Error).message}.`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex flex-col gap-s7">
      <header className="flex items-end justify-between gap-s5 flex-wrap">
        <div>
          <h1 className="t-overline mb-s3">Estoque</h1>
          <p className="font-display italic text-[40px] leading-tight text-fg-1 tracking-[-0.02em]">
            O inventário do jardim.
          </p>
        </div>
        <div className="flex items-center gap-s3">
          <label className="flex items-center gap-s2 font-body text-[12px] text-fg-3 cursor-pointer">
            <input
              type="checkbox"
              checked={mostrarInativos}
              onChange={(e) => setMostrarInativos(e.target.checked)}
            />
            Mostrar inativos
          </label>
          <div className="w-[240px] relative">
            <Search size={14} strokeWidth={1.6} className="absolute left-s3 top-1/2 -translate-y-1/2 text-fg-3 pointer-events-none" />
            <input
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              placeholder="Buscar produto…"
              className="w-full bg-input border border-[var(--border)] rounded-sm pl-s7 pr-s4 h-[40px] font-body text-[13px] text-fg-1 outline-none focus:border-moss-400"
            />
          </div>
        </div>
      </header>

      <section className="grid grid-cols-3 gap-s5">
        <KPITile label="Produtos ativos" valor={kpis.ativos} />
        <KPITile label="Em baixo estoque" valor={kpis.baixo} hint={kpis.baixo > 0 ? 'repor logo' : 'tudo ok'} />
        <KPITile label="Valor em estoque" valor={kpis.valor} moeda />
      </section>

      {/* novo produto */}
      <form
        onSubmit={onCriar}
        className="bg-panel border border-[var(--border)] rounded-md p-s5 grid grid-cols-[2fr_1.2fr_1fr_0.8fr_1fr_1fr_auto] gap-s3 items-end"
      >
        <Campo label="Produto"><input className={inputCls} value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Buquê de rosas" /></Campo>
        <Campo label="Categoria"><input className={inputCls} value={categoria} onChange={(e) => setCategoria(e.target.value)} placeholder="Flores" /></Campo>
        <Campo label="Preço"><input className={inputCls} value={preco} onChange={(e) => setPreco(e.target.value)} placeholder="120" inputMode="decimal" /></Campo>
        <Campo label="Unidade"><input className={inputCls} value={unidade} onChange={(e) => setUnidade(e.target.value)} placeholder="un" /></Campo>
        <Campo label="Estoque"><input className={inputCls} value={estoque} onChange={(e) => setEstoque(e.target.value)} placeholder="0" inputMode="decimal" /></Campo>
        <Campo label="Mínimo"><input className={inputCls} value={minimo} onChange={(e) => setMinimo(e.target.value)} placeholder="0" inputMode="decimal" /></Campo>
        <Button type="submit" variant="primary" disabled={criando} leading={<Plus size={14} strokeWidth={1.6} />}>
          {criando ? '…' : 'Add'}
        </Button>
      </form>

      {/* tabela */}
      <div className="bg-panel border border-[var(--border)] rounded-md overflow-hidden">
        <div className="grid grid-cols-[2fr_1fr_1.6fr_0.8fr_1fr_1fr] gap-s4 px-s5 py-s3 border-b border-[var(--border-subtle)] t-overline">
          <span>Produto</span>
          <span>Preço</span>
          <span>Estoque</span>
          <span>Mínimo</span>
          <span>Status</span>
          <span className="text-right">Ações</span>
        </div>
        {visiveis.length === 0 ? (
          <p className="font-display italic text-[18px] text-fg-3 text-center py-s9">
            Catálogo vazio. Adicione um produto acima.
          </p>
        ) : (
          visiveis.map((p) => (
            <div
              key={p.id}
              className={cn(
                'grid grid-cols-[2fr_1fr_1.6fr_0.8fr_1fr_1fr] gap-s4 px-s5 py-s3 border-b border-[var(--border-subtle)] last:border-0 items-center',
                !p.ativo && 'opacity-40',
                p.ativo && p.baixo && 'bg-[rgba(192,138,62,0.06)]',
              )}
            >
              <div className="min-w-0">
                <div className="font-body font-semibold text-[13.5px] text-fg-1 truncate">{p.nome}</div>
                {p.categoria && <div className="font-body text-[11px] text-fg-3">{p.categoria}</div>}
              </div>
              <Money valor={p.preco} size="md" />
              <div className="flex items-center gap-s2">
                <button
                  onClick={() => onAjustar(p, -1)}
                  disabled={busy === p.id || !p.ativo}
                  className="w-[26px] h-[26px] grid place-items-center rounded-sm border border-[var(--border)] text-fg-2 hover:text-fg-1 hover:border-[var(--border-strong)] disabled:opacity-30"
                  aria-label="diminuir"
                >
                  <Minus size={13} strokeWidth={2} />
                </button>
                <span className="font-mono text-[14px] text-fg-1 tnum min-w-[52px] text-center">
                  {p.estoque} <span className="text-[10px] text-fg-3">{p.unidade}</span>
                </span>
                <button
                  onClick={() => onAjustar(p, 1)}
                  disabled={busy === p.id || !p.ativo}
                  className="w-[26px] h-[26px] grid place-items-center rounded-sm border border-[var(--border)] text-fg-2 hover:text-fg-1 hover:border-[var(--border-strong)] disabled:opacity-30"
                  aria-label="aumentar"
                >
                  <Plus size={13} strokeWidth={2} />
                </button>
              </div>
              <span className="font-mono text-[12px] text-fg-3 tnum">{p.estoque_minimo}</span>
              <div>
                {!p.ativo ? (
                  <Pill tone="mute" dot={false}>Inativo</Pill>
                ) : p.baixo ? (
                  <Pill tone="amber" dot>Baixo</Pill>
                ) : (
                  <Pill tone="moss" dot>OK</Pill>
                )}
              </div>
              <div className="flex justify-end items-center gap-s3">
                <button
                  onClick={() => setEditando(p)}
                  title="Editar"
                  aria-label="Editar"
                  className="inline-flex items-center justify-center h-[26px] w-[26px] rounded-sm border border-[var(--border)] text-fg-3 hover:text-fg-1 hover:border-[var(--border-strong)] transition-colors"
                >
                  <Pencil size={13} strokeWidth={1.6} />
                </button>
                <button
                  onClick={() => onToggleAtivo(p)}
                  disabled={busy === p.id}
                  className="font-body text-[12px] text-fg-3 hover:text-fg-1 transition-colors duration-fast disabled:opacity-40"
                >
                  {p.ativo ? 'Desativar' : 'Reativar'}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <p className="font-body text-[11px] text-fg-3 inline-flex items-center gap-s2">
        <Package size={12} strokeWidth={1.6} />
        A baixa de estoque é automática quando um pedido casa (por nome) com um produto do catálogo. Cancelar/excluir o pedido devolve ao estoque.
      </p>

      {editando && (
        <EditarModal
          produto={editando}
          onClose={() => setEditando(null)}
          onSaved={(p) => {
            substituir(p);
            setEditando(null);
            toast('success', `${p.nome} atualizado.`);
          }}
        />
      )}
    </div>
  );
}

function EditarModal({
  produto,
  onClose,
  onSaved,
}: {
  produto: Produto;
  onClose: () => void;
  onSaved: (p: Produto) => void;
}) {
  const [nome, setNome] = useState(produto.nome);
  const [categoria, setCategoria] = useState(produto.categoria || '');
  const [preco, setPreco] = useState(String(produto.preco));
  const [unidade, setUnidade] = useState(produto.unidade);
  const [estoque, setEstoque] = useState(String(produto.estoque));
  const [minimo, setMinimo] = useState(String(produto.estoque_minimo));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function salvar() {
    if (!nome.trim()) {
      setErro('Nome é obrigatório.');
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      let p = await atualizarProduto(produto.id, {
        nome: nome.trim(),
        categoria: categoria.trim() || null,
        preco: Number(preco) || 0,
        unidade: unidade.trim() || 'un',
        estoque_minimo: Number(minimo) || 0,
      });
      const novoEstoque = Number(estoque);
      if (Number.isFinite(novoEstoque) && novoEstoque !== produto.estoque) {
        p = await ajustarEstoque(produto.id, novoEstoque - produto.estoque, 'ajuste manual');
      }
      onSaved(p);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-[rgba(0,0,0,0.6)] p-s5"
      onClick={onClose}
    >
      <div
        className="bg-elev border border-[var(--border-strong)] rounded-md p-s7 w-full max-w-[460px] flex flex-col gap-s4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-display text-[24px] leading-tight text-fg-1">Editar produto</h2>
        <Campo label="Produto"><input className={inputCls} value={nome} onChange={(e) => setNome(e.target.value)} /></Campo>
        <Campo label="Categoria"><input className={inputCls} value={categoria} onChange={(e) => setCategoria(e.target.value)} placeholder="Flores" /></Campo>
        <div className="grid grid-cols-2 gap-s3">
          <Campo label="Preço"><input className={inputCls} value={preco} onChange={(e) => setPreco(e.target.value)} inputMode="decimal" /></Campo>
          <Campo label="Unidade (un / g)"><input className={inputCls} value={unidade} onChange={(e) => setUnidade(e.target.value)} /></Campo>
          <Campo label="Estoque"><input className={inputCls} value={estoque} onChange={(e) => setEstoque(e.target.value)} inputMode="decimal" /></Campo>
          <Campo label="Mínimo"><input className={inputCls} value={minimo} onChange={(e) => setMinimo(e.target.value)} inputMode="decimal" /></Campo>
        </div>
        {erro && <span className="font-body text-[12px] text-petal-300">{erro}</span>}
        <div className="flex justify-end gap-s3 mt-s2">
          <button
            onClick={onClose}
            className="h-[38px] px-s5 rounded-sm border border-[var(--border)] text-fg-2 text-[13px] font-semibold hover:text-fg-1 hover:border-[var(--border-strong)]"
          >
            Cancelar
          </button>
          <Button variant="primary" onClick={salvar} disabled={salvando}>
            {salvando ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Campo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-s1">
      <span className="t-overline">{label}</span>
      {children}
    </label>
  );
}
